import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixIdentityV1Contributor from './identity-v1-contributor-contributors.http.js';

/** A contributor represents a Wix user with edit or manage access to a site owned by a separate Wix account. Contributors can be assigned roles that determine their level of access to the site and its assets. */
export interface Contributor {
  /** Contributor's metadata, including account ID, name, email, and role assignments. */
  metaData?: PersonMetaData;
  /** Whether the contributor account is a team account. */
  isTeam?: boolean | null;
  /** Date that the contributor joined the site. */
  joinedAt?: Date | null;
  /** Email address that received the invite. */
  invitedEmail?: string | null;
  /** Whether the contributor account is a client account. */
  isClient?: boolean | null;
  /**
   * Contributor's user ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * A set of capability keys representing the actions that the caller is allowed
   * to perform on this specific contributor. This field is calculated based on the
   * identity of the request sender and may differ between callers.
   * @maxSize 20
   * @maxLength 20
   */
  callerCapabilities?: string[];
}

export interface PersonMetaData {
  /** Contributor's account ID. */
  _id?: string;
  /** Contributor's full name. */
  fullName?: string | null;
  /** URL for contributor's profile image. */
  imageUrl?: string | null;
  /** Contributor's email address. */
  email?: string | null;
  /** Contributor's role assignments, mapping their access to site assets and their assigned roles. */
  assignments?: Assignment[];
}

export interface Assignment {
  /** Role assigned to the user. */
  policy?: AssignedPolicy;
  /** Unique ID for this specific assignment. */
  assignmentId?: string;
  /** Identity assigned to the asset in an assignment, referred to as subject. Supported subjects include user IDs, account IDs, and app IDs. */
  subject?: Subject;
}

export interface AssignedPolicy {
  /** Role ID. */
  policyId?: string;
  /** Role title. */
  title?: string | null;
  /** Role description. */
  description?: string | null;
}

export interface Restriction extends RestrictionRestrictionsOneOf {
  /**
   * Deprecated.
   * @deprecated
   */
  resource?: Resource;
  /** List of conditions restricting the user's access. Currently only folder conditions are supported. */
  conditions?: Conditions;
  /** Site where the assignment restrictions apply. */
  site?: SiteRestriction;
  /** Business locations where the assignment restrictions apply. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction). */
  locations?: LocationsRestriction;
}

/** @oneof */
export interface RestrictionRestrictionsOneOf {
  /**
   * Deprecated.
   * @deprecated
   */
  resource?: Resource;
  /** List of conditions restricting the user's access. Currently only folder conditions are supported. */
  conditions?: Conditions;
  /** Site where the assignment restrictions apply. */
  site?: SiteRestriction;
  /** Business locations where the assignment restrictions apply. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction). */
  locations?: LocationsRestriction;
}

export interface Resource {
  /** Resource type. */
  resourceType?: ResourceTypeWithLiterals;
  /** Resource ID. */
  _id?: string;
  value?: string | null;
}

export enum ResourceType {
  UNKNOWN_RESOURCE_TYPE = 'UNKNOWN_RESOURCE_TYPE',
  SITE = 'SITE',
}

/** @enumType */
export type ResourceTypeWithLiterals =
  | ResourceType
  | 'UNKNOWN_RESOURCE_TYPE'
  | 'SITE';

export interface Conditions {
  /** List of conditions. */
  conditions?: Condition[];
}

export interface Condition {
  /** Condition type. */
  conditionType?: ConditionAttributeTypeWithLiterals;
  /** Condition ID. */
  _id?: string;
  /** Expected value of the condition. When `conditionType` = "FOLDER", this is the folder path. */
  value?: string | null;
}

export enum ConditionAttributeType {
  UNKNOWN_CONDITION_TYPE = 'UNKNOWN_CONDITION_TYPE',
  FOLDER = 'FOLDER',
}

/** @enumType */
export type ConditionAttributeTypeWithLiterals =
  | ConditionAttributeType
  | 'UNKNOWN_CONDITION_TYPE'
  | 'FOLDER';

export interface SiteRestriction {
  /** Site ID. */
  _id?: string;
  /** Site name. */
  value?: string | null;
}

export interface CompanionResource {
  /** Asset ID (referred to here as resource ID). */
  _id?: string;
  /** Asset type (referred to here as resource type). as predefined in the authorization system */
  resourceType?: string;
}

export interface LocationsRestriction {
  /**
   * Location IDs where the assignment restrictions apply.
   * @format GUID
   * @maxSize 20
   */
  ids?: string[];
}

export interface Subject {
  /** ID of identity assigned to the asset. */
  _id?: string;
  /** Type of identity assigned to the asset. Supported subject types include user IDs, account IDs, and app IDs. */
  subjectType?: SubjectTypeWithLiterals;
  /** Context of identity assigned to the asset. For example, a `subjectType` = `USER` will have `context` = `ACCOUNT`. */
  context?: SubjectContext;
}

export enum SubjectType {
  UNKNOWN = 'UNKNOWN',
  ACCOUNT = 'ACCOUNT',
  USER = 'USER',
  USER_GROUP = 'USER_GROUP',
  MEMBER_GROUP = 'MEMBER_GROUP',
  VISITOR_GROUP = 'VISITOR_GROUP',
  EXTERNAL_APP = 'EXTERNAL_APP',
  ACCOUNT_GROUP = 'ACCOUNT_GROUP',
  WIX_APP = 'WIX_APP',
}

/** @enumType */
export type SubjectTypeWithLiterals =
  | SubjectType
  | 'UNKNOWN'
  | 'ACCOUNT'
  | 'USER'
  | 'USER_GROUP'
  | 'MEMBER_GROUP'
  | 'VISITOR_GROUP'
  | 'EXTERNAL_APP'
  | 'ACCOUNT_GROUP'
  | 'WIX_APP';

export interface SubjectContext {
  _id?: string;
  contextType?: SubjectContextTypeWithLiterals;
}

export enum SubjectContextType {
  UNKNOWN_CTX = 'UNKNOWN_CTX',
  ORG_CTX = 'ORG_CTX',
  ACCOUNT_CTX = 'ACCOUNT_CTX',
}

/** @enumType */
export type SubjectContextTypeWithLiterals =
  | SubjectContextType
  | 'UNKNOWN_CTX'
  | 'ORG_CTX'
  | 'ACCOUNT_CTX';

export interface GetAppContributorsRequest {
  /** @format GUID */
  appId?: string;
  /** The locale of the request. Defaults to en-us. */
  locale?: string | null;
}

export interface GetAppContributorsResponse {
  contributors?: Contributor[];
  invites?: AppInvite[];
}

export interface AppInvite {
  /**
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * TODO: amitis - remove this comment after the next merge
   * @format EMAIL
   */
  destEmail?: string;
  /**
   * @readonly
   * @maxLength 20
   */
  status?: string;
  /**
   * @format WEB_URL
   * @readonly
   */
  acceptLink?: string;
  /** @maxLength 50 */
  invitePurpose?: string | null;
  /** @maxSize 10 */
  policies?: AssignedPolicy[];
  /** @readonly */
  expirationDate?: Date | null;
  /** @readonly */
  dateCreated?: Date | null;
  /** @readonly */
  dateUpdated?: Date | null;
  /**
   * A set of capability keys representing the actions that the caller is allowed
   * to perform on this specific invite. This field is calculated based on the
   * identity of the request sender and may differ between callers.
   * @maxSize 20
   * @maxLength 20
   */
  callerCapabilities?: string[];
}

export interface GetSiteContributorsRequest {
  /** The locale of the request. Defaults to en-us */
  locale?: string | null;
}

export interface GetSiteContributorsResponse {
  users?: User[];
  teams?: Team[];
  invites?: SiteInvite[];
  policies?: Policy[];
  permissions?: string[];
  userId?: string;
  loggedInAccountId?: string;
  pendingOwner?: PendingOwner;
  contributorLimit?: ContributorLimit;
  predefinedRoles?: PredefinedRoles;
}

export interface User {
  /** User ID. */
  _id?: string;
  /**
   * Deprecated.
   * @deprecated
   */
  roles?: string[];
  /** User's email address. */
  email?: string;
  /** User's name. */
  name?: Name;
  /** URL to user's profile image, when provided. */
  profileImage?: string | null;
  /** Date the user joined the team. */
  joinedTeamAt?: Date | null;
  /**
   * Deprecated.
   * @deprecated
   */
  policyIds?: string[];
  /** Resources the user can access. */
  assignments?: Assignment[];
}

export interface Name {
  /** User's first name. */
  firstName?: string;
  /** User's last name. */
  lastName?: string;
}

export interface Team {
  accountId?: string;
  accountInfo?: ApiAccountInfo;
  policyIds?: string[];
  joinedAt?: Date | null;
}

export interface ApiAccountInfo {
  accountName?: string;
  accountImage?: string;
  isTeam?: boolean;
}

export interface SiteInvite {
  /**
   * Invite ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Site ID the user is invited to as a collaborator.
   * @format GUID
   * @readonly
   */
  siteId?: string;
  /**
   * Email address where the invite was sent.
   * @format EMAIL
   */
  email?: string;
  /** Role IDs included in the invite. */
  policyIds?: string[];
  /**
   * Deprecated. Use `inviterAccountId`.
   * @readonly
   * @deprecated
   */
  inviterId?: string;
  /**
   * Invite Status.
   *
   * Supported values:
   * - **Pending:** The invite has been sent and is valid, waiting for the user's response.
   * - **Used:** The invite has been accepted.
   * - **Deleted:** The invite has been deleted or revoked.
   * - **Declined:** The user declined the invite.
   * - **Expired:** The invite has expired without being accepted.
   */
  status?: InviteStatusWithLiterals;
  /** Link to accept the invite. */
  acceptLink?: string;
  /**
   * Inviting account ID.
   * @format GUID
   * @readonly
   */
  inviterAccountId?: string;
  /**
   * Account ID that accepted the invite. Populated only once the invite is accepted.
   * @format GUID
   * @readonly
   */
  acceptedByAccountId?: string | null;
  /** Date the invite was created. */
  dateCreated?: Date | null;
  /**
   * User's Wix Bookings staff ID, if relevant.
   * @format GUID
   */
  staffId?: string | null;
  /** Invite expiration date */
  expirationDate?: Date | null;
  /**
   * Location ids included in the invite
   * @format GUID
   * @readonly
   * @maxSize 20
   */
  locationIds?: string[];
  /**
   * A set of capability keys representing the actions that the caller is allowed
   * to perform on this specific contributor. This field is calculated based on the
   * identity of the request sender and may differ between callers.
   * @maxSize 20
   * @maxLength 20
   */
  callerCapabilities?: string[];
}

/** Invite status stating whether the invite was accepted, waiting to be accepted, deleted etc.. */
export enum InviteStatus {
  Pending = 'Pending',
  Used = 'Used',
  Deleted = 'Deleted',
  Declined = 'Declined',
  Expired = 'Expired',
}

/** @enumType */
export type InviteStatusWithLiterals =
  | InviteStatus
  | 'Pending'
  | 'Used'
  | 'Deleted'
  | 'Declined'
  | 'Expired';

export interface Policy {
  _id?: string;
  description?: string | null;
  name?: string | null;
  isCustom?: boolean;
  scopes?: string[];
}

export interface PendingOwner {
  email?: string;
  expirationDate?: Date | null;
  acceptLink?: string;
}

export interface ContributorLimit {
  contributorLimit?: number;
}

export interface PredefinedRoles {
  roles?: PredefinedRole[];
}

export interface PredefinedRole {
  titleKey?: string;
  roles?: Role[];
  title?: string | null;
  areaId?: string;
}

export interface Role {
  _id?: string;
  deprecatedKey?: string;
  /** @deprecated */
  titleKey?: string;
  /** @deprecated */
  descriptionKey?: string;
  deprecated?: boolean;
  restrictFromLevel?: string;
  experiments?: string[];
  appDefIds?: string[];
  title?: string | null;
  description?: string | null;
  isCustom?: boolean;
  scopes?: string[];
  availableResourceTypes?: ResourceTypeWithLiterals[];
  availableConditions?: ConditionAttributeTypeWithLiterals[];
  limitToEditorTypes?: string[];
}

export interface GetSiteContributorsV2Request {
  /** The locale of the request. Defaults to en-us. */
  locale?: string | null;
  /** The set of desired fields. */
  fields?: string[];
}

export interface GetSiteContributorsV2Response {
  /** List of contributors of the given site. */
  contributors?: Contributor[];
  /** List of invites to contribute to the given site. */
  invites?: SiteInvite[];
  /** Quota information for contributors on the given site. */
  contributorsQuota?: ContributorsQuota;
  /** Pending owner information for the site. */
  pendingOwner?: PendingOwner;
  /**
   * Enriched invites with localized policy information (flattened structure)
   * @maxSize 20
   */
  enrichedInvites?: EnrichedSiteInvite[];
}

export interface ContributorsQuota extends ContributorsQuotaOptionsOneOf {
  /** Limited contributors quota details. */
  limitedOptions?: LimitedOptions;
  /** Type of contributors quota */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface ContributorsQuotaOptionsOneOf {
  /** Limited contributors quota details. */
  limitedOptions?: LimitedOptions;
}

/** Enum to represent different types of contributors quota. */
export enum Type {
  UNKNOWN = 'UNKNOWN',
  LIMITED = 'LIMITED',
  UNLIMITED = 'UNLIMITED',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNKNOWN' | 'LIMITED' | 'UNLIMITED';

/** Details for a limited contributors quota. */
export interface LimitedOptions {
  /** Maximum number of contributors allowed. */
  limit?: number;
  /** Number of accepted or pending invitations. */
  used?: number;
}

/** Enriched site invite with flattened structure including all invite fields plus localized policy info */
export interface EnrichedSiteInvite {
  /**
   * Core invite fields (flattened from SiteInvite)
   * @format GUID
   */
  _id?: string;
  /** @format EMAIL */
  email?: string;
  /** @maxLength 50 */
  acceptLink?: string;
  dateCreated?: Date | null;
  /**
   * @maxSize 20
   * @maxLength 20
   */
  policyIds?: string[];
  /**
   * Additional invite fields that may exist
   * @maxLength 50
   */
  inviterId?: string;
  /** @maxLength 50 */
  siteId?: string;
  /**
   * Additional SiteInvite fields
   * @maxLength 50
   */
  status?: string;
  expirationDate?: Date | null;
  /**
   * @format GUID
   * @maxSize 20
   */
  locationIds?: string[];
  /**
   * Enriched policy information with localized titles and descriptions
   * @maxSize 20
   */
  policies?: LocalizedPolicyInfo[];
}

/** Localized policy information for a specific policy */
export interface LocalizedPolicyInfo {
  /**
   * Policy ID (role ID)
   * @maxLength 50
   */
  policyId?: string;
  /**
   * Localized policy title
   * @maxLength 500
   */
  title?: string;
  /**
   * Localized policy description
   * @maxLength 1000
   */
  description?: string;
}

export interface GetCurrentContributorRequest {}

export interface GetCurrentContributorResponse {
  /**
   * The contributor details for the current caller, including metadata, role assignments, and capabilities.
   * Some fields may be omitted based on the caller's permissions.
   */
  contributor?: Contributor;
}

export interface HandleSiteTransferRequest {
  originalOwnerAccountId?: string;
  newOwnerAccountId?: string;
  metaSiteId?: string;
  keepOriginalOwnerAsContributor?: boolean;
}

export interface HandleSiteTransferResponse {}

export interface GetCurrentUserRolesRequest {
  /** The locale of the request. Defaults to en-us */
  locale?: string | null;
}

export interface GetCurrentUserRolesResponse {
  roles?: LocalizedRole[];
}

export interface LocalizedRole {
  name?: string;
  description?: string | null;
}

export interface BulkGetUserRolesOnSiteRequest {
  /** @maxSize 5 */
  users?: UserSubject[];
  /** The locale of the request. Defaults to en-us */
  locale?: string | null;
}

export interface UserSubject {
  userId?: string;
  accountId?: string;
}

export interface BulkGetUserRolesOnSiteResponse {
  userRoles?: UserLocalizedRoles[];
}

export interface UserLocalizedRoles {
  user?: UserSubject;
  roles?: LocalizedRole[];
}

export interface BulkValidateEmailInviteEligibilityRequest {
  /**
   * List of emails to be checked for invite eligibility.
   * @minSize 1
   * @maxSize 10
   * @format EMAIL
   */
  emails?: string[];
}

export interface BulkValidateEmailInviteEligibilityResponse {
  /** List of email invite eligibility results. */
  emailsEligibility?: EmailInviteEligibility[];
}

export interface EmailInviteEligibility {
  /**
   * The email address being checked.
   * @format EMAIL
   */
  email?: string;
  /** Whether the email is eligible for an invite. */
  eligible?: boolean;
}

export interface ChangeContributorRoleRequest {
  /**
   * Contributor's account ID.
   * @format GUID
   */
  accountId: string;
  /** New roles to assign to the contributor on the site. Replaces all existing role assignments. */
  newRoles: SiteRoleAssignment[];
}

export interface SiteRoleAssignment {
  /** Role ID. Sometimes referred to as policy ID. See [Roles and Permissions](https://support.wix.com/en/article/roles-permissions-overview) for a list of available roles. */
  roleId?: string;
  /**
   * Assignment ID mapping the role to the contributor on the site.
   * @readonly
   */
  assignmentId?: string;
}

export interface ChangeContributorRoleResponse {
  /** Roles assigned to the contributor on the site. */
  newAssignedRoles?: SiteRoleAssignment[];
}

export interface ChangeContributorLocationRequest {
  /**
   * Contributor's account ID.
   * @format GUID
   */
  accountId: string;
  /**
   * Location IDs to assign to the contributor's role assignments on the site. Replaces all existing location assignments. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
   * @format GUID
   * @maxSize 20
   */
  newLocations: string[];
}

export interface ChangeContributorLocationResponse {
  /**
   * Locations assigned to the contributor's role assignments on the site.
   * @maxSize 20
   */
  newAssignedLocations?: SiteLocationAssignment[];
}

export interface SiteLocationAssignment {
  /**
   * Location IDs assigned to the contributor's role assignments.
   * @format GUID
   * @maxSize 100
   */
  locationIds?: string[];
  /**
   * Assignment IDs mapping the locations to the contributor's role assignments on the site.
   * @readonly
   * @maxLength 20
   * @maxSize 999
   */
  assignmentIds?: string[];
}

export interface QuerySiteContributorsRequest {
  /** Filter object for narrowing search results. */
  filter?: QuerySiteContributorsFilter;
}

export interface QuerySiteContributorsFilter {
  /**
   * Role IDs (referred to here as policy IDs) to filter by. Returns only contributors with at least one of the specified roles. See [Roles and Permissions](https://support.wix.com/en/article/roles-permissions-overview) for available roles.
   * @maxSize 20
   */
  policyIds?: string[];
  /**
   * Location IDs to filter by. Returns only contributors with assignments to at least one of the specified locations.
   * @format GUID
   * @maxSize 20
   */
  locationIds?: string[];
}

export enum FieldSet {
  UNKNOWN = 'UNKNOWN',
  /** Include only `account_id` and `account_owner_id` fields. */
  META_DATA = 'META_DATA',
}

/** @enumType */
export type FieldSetWithLiterals = FieldSet | 'UNKNOWN' | 'META_DATA';

export interface QuerySiteContributorsResponse {
  /** List of site contributors matching the filter criteria. */
  contributors?: ContributorV2[];
}

export interface ContributorV2 {
  /** Contributor's account ID. */
  accountId?: string | null;
  /** User ID of the owner of the account that the contributor belongs to. */
  accountOwnerId?: string | null;
}

export interface GetContributorsQuotaRequest {}

export interface GetContributorsQuotaResponse {
  /** Quota information for contributors on the given site. */
  contributorsQuota?: ContributorsQuota;
}

export interface RemoveContributorRequest {
  /**
   * Contributor's account ID to remove from the site.
   * @format GUID
   */
  accountId: string;
}

export interface RemoveContributorResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ContributorCreatedEnvelope {
  entity: Contributor;
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Challenges
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Portfolio
 * @permissionScopeId SCOPE.PORTFOLIO.MANAGE-PORTFOLIO
 * @permissionScope View SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.VIEW-SEO
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Manage Contributors
 * @permissionScopeId SCOPE.DC-IDENTITY.MANAGE-CONTRIBUTORS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionId site-users.view-users
 * @webhook
 * @eventType wix.identity.v1.contributor_created
 * @serviceIdentifier com.wixpress.roles.management.api.SiteRolesManagementService
 * @slug created
 * @documentationMaturity preview
 */
export declare function onContributorCreated(
  handler: (event: ContributorCreatedEnvelope) => void | Promise<void>
): void;

/**
 * returns a list of all the contributors + invites to be contributors of a given meta site ID taken from the
 * context.
 * @internal
 * @documentationMaturity preview
 * @permissionId site-users.view-users
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.GetSiteContributorsV2
 */
export async function getSiteContributorsV2(
  options?: GetSiteContributorsV2Options
): Promise<
  NonNullablePaths<
    GetSiteContributorsV2Response,
    | `contributors`
    | `contributors.${number}.metaData._id`
    | `contributors.${number}._id`
    | `invites`
    | `invites.${number}._id`
    | `invites.${number}.siteId`
    | `invites.${number}.email`
    | `invites.${number}.inviterId`
    | `invites.${number}.status`
    | `invites.${number}.acceptLink`
    | `invites.${number}.inviterAccountId`
    | `contributorsQuota.limitedOptions.limit`
    | `contributorsQuota.limitedOptions.used`
    | `contributorsQuota.type`
    | `pendingOwner.email`
    | `pendingOwner.acceptLink`
    | `enrichedInvites`
    | `enrichedInvites.${number}._id`
    | `enrichedInvites.${number}.email`
    | `enrichedInvites.${number}.acceptLink`
    | `enrichedInvites.${number}.inviterId`
    | `enrichedInvites.${number}.siteId`
    | `enrichedInvites.${number}.status`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    locale: options?.locale,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixIdentityV1Contributor.getSiteContributorsV2(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          locale: '$[0].locale',
          fields: '$[0].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetSiteContributorsV2Options {
  /** The locale of the request. Defaults to en-us. */
  locale?: string | null;
  /** The set of desired fields. */
  fields?: string[];
}

/**
 * Returns the current caller contributor's details for this site, including their roles and access.
 * The site is determined from the caller's authentication context.
 * If the caller is not a contributor to the site, a `NOT_FOUND` error is returned.
 * @internal
 * @documentationMaturity preview
 * @permissionId SITE_ROLES.GET_CURRENT_CONTRIBUTOR
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.GetCurrentContributor
 */
export async function getCurrentContributor(): Promise<
  NonNullablePaths<
    GetCurrentContributorResponse,
    | `contributor.metaData._id`
    | `contributor.metaData.assignments`
    | `contributor.metaData.assignments.${number}.policy.policyId`
    | `contributor.metaData.assignments.${number}.assignmentId`
    | `contributor.metaData.assignments.${number}.subject._id`
    | `contributor.metaData.assignments.${number}.subject.subjectType`
    | `contributor.metaData.assignments.${number}.subject.context._id`
    | `contributor.metaData.assignments.${number}.subject.context.contextType`
    | `contributor._id`
    | `contributor.callerCapabilities`,
    8
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixIdentityV1Contributor.getCurrentContributor(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates all of a contributor's roles for the specified site, overriding any existing roles.
 *
 * Replaces all existing role assignments for the contributor with the roles specified in `newRoles`. To retrieve available roles, call [Get Roles Info](https://dev.wix.com/docs/api-reference/account-level/user-management/accounts/users/get-roles-info).
 * @param accountId - Contributor's account ID.
 * @public
 * @requiredField accountId
 * @requiredField options
 * @requiredField options.newRoles
 * @param options - Filter options. The `newRoles` field **must** be passed.
 * @permissionId SITE_ROLES.CHANGE_ROLE
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.ChangeRole
 */
export async function changeRole(
  accountId: string,
  options: NonNullablePaths<ChangeRoleOptions, `newRoles`, 2>
): Promise<
  NonNullablePaths<
    ChangeContributorRoleResponse,
    | `newAssignedRoles`
    | `newAssignedRoles.${number}.roleId`
    | `newAssignedRoles.${number}.assignmentId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    accountId: accountId,
    newRoles: options?.newRoles,
  });

  const reqOpts = ambassadorWixIdentityV1Contributor.changeRole(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          accountId: '$[0]',
          newRoles: '$[1].newRoles',
        },
        singleArgumentUnchanged: false,
      },
      ['accountId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ChangeRoleOptions {
  /** New roles to assign to the contributor on the site. Replaces all existing role assignments. */
  newRoles: SiteRoleAssignment[];
}

/**
 * Updates the business locations within all a contributor's role assignments for the specified site, overriding any existing locations.
 *
 * Replaces all existing location assignments for the contributor's roles with the locations specified in `newLocations`. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
 * @param accountId - Contributor's account ID.
 * @public
 * @requiredField accountId
 * @requiredField options
 * @requiredField options.newLocations
 * @permissionId SITE_ROLES.CHANGE_LOCATION
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.ChangeContributorLocation
 */
export async function changeContributorLocation(
  accountId: string,
  options: NonNullablePaths<ChangeContributorLocationOptions, `newLocations`, 2>
): Promise<
  NonNullablePaths<ChangeContributorLocationResponse, `newAssignedLocations`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    accountId: accountId,
    newLocations: options?.newLocations,
  });

  const reqOpts =
    ambassadorWixIdentityV1Contributor.changeContributorLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          accountId: '$[0]',
          newLocations: '$[1].newLocations',
        },
        singleArgumentUnchanged: false,
      },
      ['accountId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ChangeContributorLocationOptions {
  /**
   * Location IDs to assign to the contributor's role assignments on the site. Replaces all existing location assignments. Get location IDs from the [Locations API](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
   * @format GUID
   * @maxSize 20
   */
  newLocations: string[];
}

/**
 * Retrieves a list of contributors for the specified site, given the provided filters.
 *
 * Returns contributor account IDs and account owner IDs that match the specified filters.
 * @public
 * @param options - Filter options.
 * @permissionId site-users.view-users
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.QuerySiteContributors
 */
export async function querySiteContributors(
  options?: QuerySiteContributorsOptions
): Promise<NonNullablePaths<QuerySiteContributorsResponse, `contributors`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts =
    ambassadorWixIdentityV1Contributor.querySiteContributors(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QuerySiteContributorsOptions {
  /** Filter object for narrowing search results. */
  filter?: QuerySiteContributorsFilter;
}

/**
 * returns the quota information for contributors on the given site.
 * @internal
 * @documentationMaturity preview
 * @permissionId site-users.view-users
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.GetContributorsQuota
 */
export async function getContributorsQuota(): Promise<
  NonNullablePaths<
    GetContributorsQuotaResponse,
    | `contributorsQuota.limitedOptions.limit`
    | `contributorsQuota.limitedOptions.used`
    | `contributorsQuota.type`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixIdentityV1Contributor.getContributorsQuota(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes a contributor from the site by clearing all their role assignments.
 * @param accountId - Contributor's account ID to remove from the site.
 * @public
 * @requiredField accountId
 * @permissionId SITE_ROLES.REMOVE_CONTRIBUTOR
 * @applicableIdentity APP
 * @fqn com.wixpress.roles.management.api.SiteRolesManagementService.RemoveContributor
 */
export async function removeContributor(accountId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    accountId: accountId,
  });

  const reqOpts = ambassadorWixIdentityV1Contributor.removeContributor(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { accountId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['accountId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
